import { BaseLogo } from "../../core/models/entities/BaseLogo";
import { ApprovalStatus } from "../../core/models/entities/Approval";
import { Role } from "../../core/models/entities/Role";
import { enviroment } from "../../enviroment";

// The approvers the API writes on a vendor verdict NO dealer placed: the
// NAV import's mirror of the supplier's AFVIST / GODKENDT ("SYSTEM",
// LogoService) and the JS upload's own verdicts ("API",
// VendorApprovalImportPolicy.JsUploadApprover - designs today, kept here as
// belt and braces). Data strings, not labels.
const IMPORT_APPROVERS: ReadonlySet<string> = new Set(["SYSTEM", "API"]);
// The API's name for the colour field in navWriteBackFields (app setting
// LogoNavWriteBackFields=method,size,colour).
const NAV_WRITE_BACK_COLOUR_FIELD = "colour";

// Flag REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED: true while the API's
// write-back worker still owes this committed NAV logo the dealer's changes
// in the supplier system (navWriteBackPending, API flag LogoNavWriteBackMode).
// Always false with the flag off, so the flag-off UI never sees the field.
export function isLogoNavWriteBackPending(
  logo: BaseLogo | null | undefined
): boolean {
  return (
    enviroment.logoRejectAndEditNavEnabled &&
    logo?.navWriteBackPending === true
  );
}

// Flag REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED: a logo already committed
// to the supplier that the dealer may still "fix themselves" under the SAME
// item number - the API says so (navWriteBackEligible: a NAV item with a
// real item number, a transfer / direct-print method, not shared with every
// customer, the caller a vendor) and no write-back is pending. Exactly true
// counts; absent (API flag off / older API) = never. Only together with the
// base flag logoRejectAndEditEnabled ("Fix it myself" itself): the NAV flag
// alone never opens an offer or an Edit, so the two flags cannot disagree.
export function canRejectAndEditCommittedNavLogo(logo: BaseLogo): boolean {
  return (
    enviroment.logoRejectAndEditEnabled &&
    enviroment.logoRejectAndEditNavEnabled &&
    logo.navWriteBackEligible === true &&
    !logo.navWriteBackPending
  );
}

// The vendor's OWN rejection: the dealer placed the verdict, so the approver
// is a user - never the import's "SYSTEM" nor the JS upload's "API". The
// API's write-back qualifies the commit on the dealer's own rejection only
// (Approval.Vendor Rejected by the caller's organisation); a rejection
// mirrored from the supplier (AFVIST, approver "SYSTEM", note "REJECTED")
// would be committed locally only, so Edit must not be offered for it. The
// API's navWriteBackEligible is REQUIRED to be false for a vendor verdict
// another organisation placed (contract to API PR B); this is the FE's
// second lock, since ApprovalDto carries no OrganizationId.
export function isVendorsOwnRejection(logo: BaseLogo): boolean {
  const vendor = logo.approval?.vendor;
  if (!vendor || vendor.status !== ApprovalStatus.Rejected) return false;
  const approver = vendor.approver;
  return (
    typeof approver === "string" &&
    approver.trim() !== "" &&
    !IMPORT_APPROVERS.has(approver.trim())
  );
}

// The popup's Edit for a committed NAV logo (Rejected layout): offered only
// after the dealer's own rejection, under the flag, while nothing is
// pending. Flag off = false = today's Edit gates, byte for byte.
export function canEditCommittedNavLogo(logo: BaseLogo): boolean {
  return canRejectAndEditCommittedNavLogo(logo) && isVendorsOwnRejection(logo);
}

// B2 (Nichlas 17/10: colours SHALL be in v1): the editor's colour tool stays
// visible for a committed NAV logo as long as the API's navWriteBackFields
// carries "colour" - or is absent. Hidden only when the API lists the fields
// it writes back and colour is not among them (the change could then never
// reach the supplier). Non-NAV logos and the flag off = never hidden.
export function isLogoColourToolHiddenForNavWriteBack(logo: BaseLogo): boolean {
  if (!enviroment.logoRejectAndEditNavEnabled) return false;
  if (logo.navWriteBackEligible !== true) return false;
  const fields = logo.navWriteBackFields;
  if (!Array.isArray(fields)) return false;
  return !fields.some(
    (field) =>
      typeof field === "string" &&
      field.trim().toLowerCase() === NAV_WRITE_BACK_COLOUR_FIELD
  );
}

// "Reject and fix it myself" for a LOGO (flag
// REACT_APP_LOGO_REJECT_AND_EDIT_ENABLED, Nichlas 17/10: «hvis det blot er en
// metode, farve eller størrelse»). Offered to the dealer only - in practice
// an admin only in act-as-vendor, where the role is Vendor; ApprovalVoting's
// showButtons never shows Approve/Reject for Role.Admin itself, so letting
// Admin through here is harmless (the customer's reject dialog is
// untouched) - and only for a logo the popup's own
// Edit is shown for - not yet committed to the supplier (a real serial as
// SKU hides Edit today: its method / size are the supplier's). The same SKU
// rule as the popup's two Edit gates. Always false with the flag off.
// Flag REACT_APP_LOGO_REJECT_AND_EDIT_NAV_ENABLED (17/10, «det skal altid
// være det samme varenummer hvis vi retter»): a COMMITTED NAV logo is
// offered too when the API says it qualifies for the write-back under the
// same item number (canRejectAndEditCommittedNavLogo). The role rule is
// unchanged; that flag off = today's SKU rule, byte for byte.
export function canLogoRejectAndEdit(logo: BaseLogo, role?: Role): boolean {
  if (!enviroment.logoRejectAndEditEnabled) return false;
  if (role === Role.EndUser) return false;
  const isCommitted =
    logo.product?.sku != null && !logo.product.sku.includes("UPLOAD_");
  return !isCommitted || canRejectAndEditCommittedNavLogo(logo);
}

// A NAV logo the dealer is FIXING THEMSELVES: the API found it eligible for
// the write-back under the same item number (navWriteBackEligible exactly
// true - with the API's ownership rule when that is on) and the vendor
// rejection is the dealer's own - the signal the editor's Edit opened on
// (canEditCommittedNavLogo, minus the flags). Shared by B3(b)'s send block
// and B2's warning, so the two never disagree about which logo is a "fix".
// No flag of its own: the callers carry theirs. Once the dealer has approved
// the fix the vendor verdict is no longer Rejected = false.
export function isRejectAndEditNavLogo(
  logo: BaseLogo | null | undefined
): boolean {
  if (!logo || logo.navWriteBackEligible !== true) return false;
  return isVendorsOwnRejection(logo);
}

// B3(b) (18/10, ANALYSE-LOGO-1102300-FARVE-NAV §3.3 H2 / §4; flag
// REACT_APP_LOGO_REJECT_AND_EDIT_BLOCK_CUSTOMER_SEND_ENABLED): the customer
// path - "Send Approval to Customer" (approval-start skip=false) and the
// customer's later verdict - never runs the API's NAV write-back; only the
// dealer's own skip approval commits the fix under the same item number. So
// while the dealer is fixing a NAV logo themselves (isRejectAndEditNavLogo)
// the editor's approval dialog blocks the send button until the dealer has
// approved the fix. Flag off = never blocked = today's dialog.
export function isCustomerSendBlockedForRejectAndEdit(logo: BaseLogo): boolean {
  if (!enviroment.logoRejectAndEditBlockCustomerSendEnabled) return false;
  return isRejectAndEditNavLogo(logo);
}

// B2 (18/10, ANALYSE-LOGO-1102300-FARVE-NAV §3.2 / §4; flag
// REACT_APP_LOGO_NAV_WRITE_BACK_SKIP_REASON_ENABLED): the texts of the
// warning shown when the dealer's skip approval of a "Fix it myself" NAV
// logo did NOT qualify for the write-back - one per reason the API's policy
// can give (LogoNavWriteBackPolicy.Qualify), plus a generic fallback that
// carries an unknown reason as the API sent it. Keys only; the handler's
// own t renders them.
const NAV_WRITE_BACK_SKIPPED_KEYS: Readonly<Record<string, string>> = {
  "shared-item": "logoList.navWriteBackSkippedSharedItem",
  "not-owned": "logoList.navWriteBackSkippedNotOwned",
  "not-rejected": "logoList.navWriteBackSkippedNotRejected",
  "vendor-not-rejected": "logoList.navWriteBackSkippedVendorNotRejected",
  "not-own-rejection": "logoList.navWriteBackSkippedNotOwnRejection",
};
const NAV_WRITE_BACK_SKIPPED_GENERIC_KEY =
  "logoList.navWriteBackSkippedGeneric";
// A warning the dealer must read: longer than the 5 s success toast.
export const NAV_WRITE_BACK_SKIPPED_TOAST_MS = 15000;

// Flag REACT_APP_LOGO_NAV_WRITE_BACK_SKIP_REASON_ENABLED: the reason the
// API's approval-start answer gives for NOT sending the fix to the supplier
// system (navWriteBackSkippedReason, API flag
// LogoNavWriteBackSkipReasonInDtoEnabled), trimmed - or null when there is
// none to show: the field absent (the commit qualified, an older API, the
// API flag off), null, blank, or not a string. The ONLY reader of the
// field; always null with the flag off, so the flag-off UI never sees it.
// The API reports the reason for EVERY NAV image logo whose skip approval
// did not qualify, whatever its status (LogoNavWriteBackPolicy
// .SkipReasonForDto: "not-rejected" is reported too), so the reason counts
// only when the logo the dealer approved - `approved`, the entity handed to
// the flow BEFORE the approval; the answer's own verdict is already Approved
// - was a "Fix it myself" NAV logo (isRejectAndEditNavLogo). A KitMaker
// draft or a NAV logo approved without a fix = null: never a false «Your fix
// ... will not be sent».
export function readNavWriteBackSkippedReason(
  approved: BaseLogo | null | undefined,
  answer: unknown
): string | null {
  if (!enviroment.logoNavWriteBackSkipReasonEnabled) return null;
  if (!isRejectAndEditNavLogo(approved)) return null;
  if (!answer || typeof answer !== "object") return null;
  const reason = (answer as { navWriteBackSkippedReason?: unknown })
    .navWriteBackSkippedReason;
  if (typeof reason !== "string") return null;
  const trimmed = reason.trim();
  return trimmed === "" ? null : trimmed;
}

// The warning's text for a reason: the known reason's own key, or the
// generic key carrying the reason as sent. The API's spelling is tolerated
// (case, blanks, underscores / spaces for hyphens) so a cosmetic change on
// the API side degrades to the generic text, never to a crash.
export function navWriteBackSkippedText(
  reason: string,
  t: (key: string, options?: Record<string, unknown>) => string
): string {
  const normalised = reason.trim().toLowerCase().replace(/[_\s]+/g, "-");
  const key = NAV_WRITE_BACK_SKIPPED_KEYS[normalised];
  return key ? t(key) : t(NAV_WRITE_BACK_SKIPPED_GENERIC_KEY, { reason });
}
